"use client";

import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";
import { cn, scrollToTop } from "@/lib/utils";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import { MOBILE_BOTTOM_TOOL_DOCK_HEIGHT } from "@/components/mobile-bottom-tools/constants";
import { replaceHashWithoutScroll } from "@workspace/gamja-platform/components/hash-highlighter";

/**
 * 글 페이지 상하 단계 이동 스텝퍼(JCG-3210) — 우하단에 ⬆️/⬇️ FAB 2개를 세로로 나란히 둔다.
 *
 * 페이지의 세로 구간을 "맨 위(top) → 문제(첫 문제) → 댓글(#comments)" 섹션으로 보고, 각 FAB 는
 * 현재 위치 기준 바로 인접한 섹션으로 이동한다.
 *  - ⬇️ 아래로: 현재 위치 바로 아래 섹션(문제 또는 댓글). 더 내려갈 곳 없으면(바닥) 숨김.
 *  - ⬆️ 위로:  현재 위치 바로 위 섹션(문제 또는 맨 위). 더 올라갈 곳 없으면(맨 위) 숨김.
 * 두 버튼을 오가며 top→문제→댓글, 댓글→문제→top 왕복 단계 이동이 된다.
 *
 * "도달" 판정은 **IntersectionObserver** 로 한다(매 스크롤 프레임 getBoundingClientRect 폴링 대신):
 * `rootMargin` 으로 뷰포트 상단 `SECTION_REACHED_VIEWPORT_RATIO`(50%) 지점에 판정선을 두고, 각 섹션
 * 앵커가 그 선 위로 올라오면(=화면에 충분히 들어오면) 도달로 본다. 그래서 첫 문제가 화면 중앙에
 * 오면 곧바로 "아래로"가 댓글로 바뀐다 — 문제 top 을 헤더 라인까지 끌어올릴 필요가 없다. IO 는
 * 판정선 교차 때만 콜백하고 리사이즈(%-margin)·레이아웃 변화도 알아서 반영해 폴링보다 견고하다.
 *
 * 앵커는 DOM 에서 직접 찾는다(문제 앵커는 지연 로드/언어 탭에 대응해 "보이는" 첫 앵커만, 댓글은
 * PostLayout 이 항상 렌더하는 `#comments` 래퍼). 문제 앵커를 못 찾으면 문제 섹션은 자동으로 빠지고,
 * `problemOnly`(왕감자시험·past-exam·문제 모음)면 처음부터 문제 섹션을 빼 아래로가 바로 댓글로 간다.
 *
 * `prefers-reduced-motion`·고정 헤더 오프셋 계약은 기존 이동 버튼과 동일하게 보존한다.
 * 무료 포함 전체 노출(맨 위로 FAB 와 동일 정책). 셸(3컬럼)/클래식/모바일 모두 window 스크롤.
 */

/** 이동 목적지 종류. */
type WaypointKind = "top" | "problem" | "comment";

/**
 * 섹션 "도달" 판정선 — 뷰포트 상단 기준 비율(0~1). 섹션 앵커 top 이 이 선 위로 올라오면 도달.
 * 0.5 = 화면 중앙. 첫 문제가 화면에 들어와 읽기 시작하는 지점에서 바로 "아래로"가 댓글로 바뀐다.
 */
const SECTION_REACHED_VIEWPORT_RATIO = 0.5;

/** 페이지 하단 댓글 섹션의 고정 스크롤 앵커 id (PostLayout 이 CommentSection 래퍼에 부여). */
const COMMENT_ANCHOR_ID = "comments";

/**
 * 이 정도 내려오면 ⬆️ "맨 위로"를 노출한다(px). 문제 전용 페이지처럼 top↔댓글 사이에 "도달" 섹션이
 * 없어도(긴 문제 영역을 스크롤하는 동안) 맨 위로가 뜨게 하는 기준. ScrollToTopButton 과 동일 임계값.
 */
const SCROLL_DOWN_THRESHOLD_PX = 200;

// scrollY 임계값 초과 여부만 구독한다(useSyncExternalStore, set-state-in-effect 없음 — ScrollToTopButton 패턴).
function subscribeScroll(callback: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener("scroll", callback, { passive: true });
  return () => window.removeEventListener("scroll", callback);
}

function getScrolledDownSnapshot(): boolean {
  return (
    typeof window !== "undefined" &&
    window.scrollY > SCROLL_DOWN_THRESHOLD_PX
  );
}

// SSR/hydration 첫 렌더는 항상 false(안 내려감) → 서버 HTML 과 일치.
function getScrolledDownServerSnapshot(): boolean {
  return false;
}

const KIND_SHORT_LABEL: Record<WaypointKind, string> = {
  top: "맨 위",
  problem: "문제",
  comment: "댓글",
};

const KIND_ARIA_LABEL: Record<WaypointKind, string> = {
  top: "맨 위로 이동",
  problem: "문제 풀이 영역으로 이동",
  comment: "댓글 영역으로 이동",
};

/** 셸 안(64px 고정 헤더)/클래식·모바일(네비바 + sticky 제목)에 맞춘 고정 헤더 오프셋. */
function getScrollOffset(inShell: boolean): number {
  if (inShell) return 64;

  const navbarHeight = window.innerWidth >= 640 ? 64 : 48;
  const stickyTitle = document.getElementById("sticky-title");
  const stickyTitleHeight =
    stickyTitle && !stickyTitle.classList.contains("hidden")
      ? stickyTitle.offsetHeight
      : 0;

  return navbarHeight + stickyTitleHeight + 16;
}

/**
 * "보이는" 첫 문제 앵커. 언어 토글 페이지는 비활성 언어 블록이 display:none 이라 위치가 0 으로
 * 잡히므로, 실제로 렌더된(offsetParent 존재 또는 client rect 있음) 앵커만 고른다.
 */
function firstVisibleProblemEl(): HTMLElement | null {
  const anchors = document.querySelectorAll<HTMLElement>('[id^="problem-"]');
  for (const el of anchors) {
    if (el.offsetParent !== null || el.getClientRects().length > 0) return el;
  }
  return null;
}

/**
 * 목적지 종류로 이동한다(클릭 시점의 실제 앵커 위치를 새로 측정해 스크롤 — 상태 지연과 무관하게
 * 항상 현재 위치 기준). 앵커 top 을 고정 헤더 오프셋만큼 띄우고, reduced-motion 이면 즉시 이동한다.
 */
function scrollToKind(kind: WaypointKind, inShell: boolean): void {
  if (kind === "top") {
    replaceHashWithoutScroll("");
    scrollToTop(); // reduced-motion 판정 포함 공용 헬퍼(@/lib/utils) 재사용.
    return;
  }

  const el =
    kind === "comment"
      ? document.getElementById(COMMENT_ANCHOR_ID)
      : firstVisibleProblemEl();
  if (!el) return;

  const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ? "auto"
    : "smooth";
  const offset = getScrollOffset(inShell);
  const top = Math.max(
    0,
    el.getBoundingClientRect().top + window.scrollY - offset,
  );
  window.scrollTo({ top, behavior });
  replaceHashWithoutScroll(`#${el.id}`);
}

function StepperFab({
  direction,
  kind,
  show,
  onClick,
}: {
  direction: "up" | "down";
  /** 이동 대상(라벨·아이콘 표시용). 실제 이동은 onClick 이 클릭 시점 위치로 다시 계산한다. */
  kind: WaypointKind;
  /** 이 방향으로 갈 섹션이 있으면 true. false 면 자리(48px)는 유지한 채 페이드아웃·비활성(inert). */
  show: boolean;
  onClick: () => void;
}) {
  const Arrow = direction === "up" ? ArrowUp : ArrowDown;
  const label = KIND_SHORT_LABEL[kind];
  const ariaLabel = KIND_ARIA_LABEL[kind];

  return (
    // 숨김은 unmount 가 아니라 inert 로 표현한다(ScrollToTopButton·mobile-bottom-tools §6-⑤ 계약):
    // 자리(48px 박스)를 유지해 형제 FAB 위치가 흔들리지 않고, inert 가 포커스·포인터를 차단하며
    // AT 트리에서 제외한다 → 마우스로 누른 직후 방향이 사라져도 "포커스 보유 중 aria-hidden" 충돌이 없다.
    <button
      type="button"
      // 방어적 blur: 클릭 프레임에 포커스를 쥔 채 곧바로 inert 가 걸리는 경합을 한 번 더 차단.
      onClick={(e) => {
        e.currentTarget.blur();
        onClick();
      }}
      aria-label={ariaLabel}
      title={ariaLabel}
      inert={!show}
      className={cn(
        "inline-flex h-12 w-12 flex-col items-center justify-center gap-0.5 rounded-full touch-manipulation",
        "border border-border bg-card/90 text-muted-foreground shadow-lg backdrop-blur",
        "transition-[opacity,transform,color,background-color,box-shadow] duration-200 ease-out",
        "hover:bg-accent hover:text-amber-600 hover:shadow-xl dark:hover:text-amber-400",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "motion-reduce:transition-none",
        // 등장/사라짐: 페이드 + 살짝 슬라이드(맨 위로 FAB 와 동일). 숨김 시 포커스/클릭 불가.
        show
          ? "translate-y-0 opacity-100"
          : "pointer-events-none translate-y-1.5 opacity-0",
      )}
    >
      <Arrow className="h-4 w-4" strokeWidth={2.5} aria-hidden />
      <span
        className="text-[10px] font-bold leading-none tracking-tight"
        aria-hidden
      >
        {label}
      </span>
    </button>
  );
}

export default function ArticleScrollStepper({
  problemOnly = false,
}: {
  /** 문제 전용 페이지(왕감자시험·past-exam·문제 모음)면 "문제" 섹션을 빼고 top↔댓글만 (JCG-3210). */
  problemOnly?: boolean;
}) {
  const { inShell } = useArticleShellSlots();
  // 각 섹션 존재 여부(present)와 도달 여부(reached). SSR/첫 렌더는 미도달 → 서버 HTML(맨 위 상태)과
  // 일치(hydration mismatch 없음). 마운트 후 IntersectionObserver 초기 콜백이 실제 위치로 보정한다.
  const [present, setPresent] = useState({ problem: false, comment: false });
  const [reached, setReached] = useState({ problem: false, comment: false });
  // 임계값 이상 내려왔는지 — 아래에 "도달" 섹션이 없어도 ⬆️ 맨 위로를 노출하기 위한 신호.
  const scrolledDown = useSyncExternalStore(
    subscribeScroll,
    getScrolledDownSnapshot,
    getScrolledDownServerSnapshot,
  );

  useEffect(() => {
    // 뷰포트 상단 SECTION_REACHED_VIEWPORT_RATIO 지점에 판정선. 섹션 앵커 top 이 그 위로 올라오면
    // (boundingClientRect.top < 판정선) 도달. IO 는 판정선 교차 때만 콜백하고 리사이즈(%-margin)도 반영.
    const io = new IntersectionObserver(
      (entries) => {
        setReached((current) => {
          let next = current;
          for (const entry of entries) {
            const kind =
              (entry.target as HTMLElement).id === COMMENT_ANCHOR_ID
                ? "comment"
                : "problem";
            const line =
              entry.rootBounds?.bottom ??
              window.innerHeight * SECTION_REACHED_VIEWPORT_RATIO;
            const isReached = entry.boundingClientRect.top < line;
            if (next[kind] !== isReached) next = { ...next, [kind]: isReached };
          }
          return next;
        });
      },
      {
        rootMargin: `0px 0px -${(1 - SECTION_REACHED_VIEWPORT_RATIO) * 100}% 0px`,
        threshold: 0,
      },
    );

    let observedProblem: HTMLElement | null = null;
    let observedComment: HTMLElement | null = null;
    let raf = 0;

    // 관찰 대상(보이는 첫 문제 앵커·댓글 앵커)이 바뀌면 IO 재관찰하고 존재/도달 상태를 정리한다.
    const sync = () => {
      raf = 0;
      const problemEl = problemOnly ? null : firstVisibleProblemEl();
      const commentEl = document.getElementById(COMMENT_ANCHOR_ID);

      if (problemEl !== observedProblem) {
        if (observedProblem) io.unobserve(observedProblem);
        if (problemEl) io.observe(problemEl);
        observedProblem = problemEl;
      }
      if (commentEl !== observedComment) {
        if (observedComment) io.unobserve(observedComment);
        if (commentEl) io.observe(commentEl);
        observedComment = commentEl;
      }

      setPresent((current) => {
        const nextPresent = { problem: !!problemEl, comment: !!commentEl };
        return current.problem === nextPresent.problem &&
          current.comment === nextPresent.comment
          ? current
          : nextPresent;
      });
      // 사라진 섹션의 도달 상태는 내려 현재 섹션 오산을 막는다(IO 는 unobserve 시 콜백하지 않음).
      setReached((current) => {
        const nextReached = {
          problem: problemEl ? current.problem : false,
          comment: commentEl ? current.comment : false,
        };
        return current.problem === nextReached.problem &&
          current.comment === nextReached.comment
          ? current
          : nextReached;
      });
    };

    const scheduleSync = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(sync);
    };

    sync();
    // 문제 지연 로드·언어 탭 전환·댓글 로드로 앵커가 생기고 사라지면 관찰 대상을 다시 잡는다.
    const mutationObserver = new MutationObserver(scheduleSync);
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mutationObserver.disconnect();
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [problemOnly]);

  // 존재하는 섹션을 위→아래 순서로 두고, 도달한 가장 깊은 섹션이 "현재 섹션". 그 위/아래가 목적지.
  // 도달은 위치상 단조(댓글 도달 ⟹ 문제 도달)라 가장 깊은 도달 섹션이 곧 현재 섹션이다.
  const order: WaypointKind[] = ["top"];
  if (present.problem) order.push("problem");
  if (present.comment) order.push("comment");
  let currentIdx = 0;
  order.forEach((kind, i) => {
    if (kind !== "top" && reached[kind]) currentIdx = Math.max(currentIdx, i);
  });
  // 현재 섹션이 top(아래에 도달 섹션 없음)이라도, 임계값 이상 내려왔으면 ⬆️ 맨 위로를 노출한다 —
  // 문제 전용 페이지(top↔댓글 사이 섹션 없음)나 이론 본문을 읽는 동안에도 맨 위로가 뜨게 한다.
  const upKind: WaypointKind | null =
    currentIdx > 0
      ? order[currentIdx - 1]
      : scrolledDown
        ? "top"
        : null;
  const downKind = currentIdx < order.length - 1 ? order[currentIdx + 1] : null;

  const handleUp = useCallback(() => {
    if (upKind) scrollToKind(upKind, inShell);
  }, [upKind, inShell]);

  const handleDown = useCallback(() => {
    if (downKind) scrollToKind(downKind, inShell);
  }, [downKind, inShell]);

  // 둘 다 갈 곳이 없으면(단일 화면에 다 들어오는 짧은 글) 아무것도 렌더하지 않는다.
  if (!upKind && !downKind) return null;

  return (
    <div
      role="group"
      aria-label="문서 단계 이동"
      style={
        {
          "--mobile-bottom-tool-dock-height": MOBILE_BOTTOM_TOOL_DOCK_HEIGHT,
        } as React.CSSProperties
      }
      className={cn(
        // 모바일/클래식: 우측 하단, 하단 dock 위로 오프셋. 데스크탑 셸(lg~)은 우측 사이드바를 덮지
        // 않도록 본문 콘텐츠 영역 우측 하단(--right-w 기준). ScrollToTopButton 과 동일 배치 규칙.
        // 아래 anchor(bottom) 기준으로 쌓여, ⬇️ 아래로가 dock 바로 위, ⬆️ 위로가 그 위에 온다.
        // 두 FAB 을 항상 렌더(자리 고정)하고 갈 곳 없는 방향만 inert+opacity-0 으로 숨긴다 → 한쪽만
        // 보일 때도 다른 쪽 자리가 유지돼 버튼이 위/아래로 튀지 않고(down 은 항상 dock 바로 위 고정),
        // mount/unmount 대신 페이드로 등장/사라진다.
        "fixed right-4 z-40 flex flex-col items-center gap-2",
        "bottom-[calc(var(--mobile-bottom-tool-dock-height,68px)+1rem+env(safe-area-inset-bottom))] lg:bottom-4",
        "lg:right-[calc(var(--right-w,0px)+1rem)]",
      )}
    >
      <StepperFab
        direction="up"
        kind={upKind ?? "top"}
        show={upKind !== null}
        onClick={handleUp}
      />
      <StepperFab
        direction="down"
        kind={downKind ?? "comment"}
        show={downKind !== null}
        onClick={handleDown}
      />
    </div>
  );
}
